import React, { createContext, ReactNode, useState } from "react";
import { LicenceTypes } from "../utils/LicenceTypes";
import { useForm } from "react-hook-form";
import axios from "axios";
import { handleFileUpload } from "../plugins/helpers";
import { PracticeTypes } from "../utils/PracticeTypes";

type MainContextProps = {
    certif: boolean;
    currentFormTab: number;
    error: boolean;
    errors: any;
    files: Files;
    handleSubmit: Function;
    licencePrice: number | undefined;
    licenceType: LicenceTypes | undefined;
    loading: boolean;
    mutation: boolean;
    numberOfParents: number;
    practice: PracticeTypes;
    register: Function;
    setCertif: Function;
    setCurrentFormTab: Function;
    setFiles: Function;
    setLicencePrice: Function;
    setLicenceType: Function;
    setLoading: Function;
    setMutation: Function;
    setNumberOfParents: Function;
    setPractice: Function;
    submit: Function;
    validateTab: Function;
};

type MainProviderProps = {
  children: ReactNode;
};

export type Files = {
    autorizationFile: File | null;
    parentalAuth: File | null;
    certif: File | null;
    healthForm: File | null;
    idPhotos: File | null;
    mutationFile: File | null;
    idDocument: File | null;
}

export const MainContext = createContext({} as MainContextProps);

const MainProvider = ({ children }: MainProviderProps) => {
    const [currentFormTab, setCurrentFormTab] = useState<number>(-1);
    const [licenceType, setLicenceType] = useState<LicenceTypes | undefined>(undefined);
    const [numberOfParents, setNumberOfParents] = useState(1);
    const [mutation, setMutation] = useState<boolean>(false);
    const [certif, setCertif] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const [licencePrice, setLicencePrice] = useState<number | undefined>(); 
    const [error, setError] = useState<boolean>(false);
    const [practice, setPractice] = useState<PracticeTypes>(PracticeTypes.COMPET);

    const [files, setFiles] = useState<Files>({
        autorizationFile: null,
        parentalAuth: null,
        certif: null,
        healthForm: null,
        idPhotos: null,
        mutationFile: null,
        idDocument: null,
    });

    const { register, handleSubmit, trigger, formState: { errors } } = useForm();

    const submit = async (data: any) => {
        validateTab();
        setCurrentFormTab(7);
        try {
            const urls = await handleFileUpload(files);
            await axios.post("https://licence-proxy.netlify.app/.netlify/functions/api/create/licence", {
                licenceType: licenceType?.toString(),
                ...data,
                mutation: mutation,
                practice: practice.toString(),
                files: urls,
            }).then((res) => {
                if (res.data.created) {
                    setLoading(false);
                    calculateLicencePrice(data);
                } else {
                    setError(true);
                    console.log(res.data.message);
                }
            });
        } catch (error) {
            console.error(error)
        }
    };

    const calculateLicencePrice = (data: any) => {
        const { birthdate } = data;
        const year = parseInt(birthdate.split("-")[0]);
        var price: number | null = null;

        if (year >= 2016) {
            price = 155;
        }

        if (year <= 2004) {
            price = 280;
            if (mutation) price += 160;
        }

        if (year === 2014 || year === 2015) {
            price = 175;
        }

        if (year === 2012 || year === 2013) {
            price = 190;
        }

        if (year === 2010 || year === 2011) {
            price = 220;
        }

        if (year === 2008 || year === 2009) {
            price = 250;
            if (mutation) price += 85;
        }

        if (year === 2005 || year === 2006 || year === 2007) {
            price = 260;
            if (mutation && year === 2007) price += 85;
            if (mutation && year < 2007) price += 160;
        }

        if (practice === PracticeTypes.DIRIGEANT) {
            price =  70;
        } else if (practice === PracticeTypes.CASUAL) {
            price = 170;
        } else if (practice === PracticeTypes.HANDFIT) {
            price = 150;
        }

        price && setLicencePrice(price);
    }

    const validateTab = async () => {
        let fields = [];
        switch (currentFormTab) {
            case 1:
                return await trigger([
                    "firstName",
                    "lastName",
                    "birthdate",
                    "address",
                    "zipcode",
                    "city",
                ]);
            case 2:
                return licenceType === LicenceTypes.YOUNG ? 
                    true : await trigger(["email", "phone"]);
            case 3:
                if (licenceType !== LicenceTypes.YOUNG) {
                    fields = ["first-urg", "last-urg", "phone-urg"]

                    return await trigger(fields);
                } else {
                    for (var i = 1;i <= numberOfParents; i++) {
                        fields.push(`first-parent-${i}`);
                        fields.push(`last-parent-${i}`);
                        fields.push(`address-parent-${i}`);
                        fields.push(`email-parent-${i}`);
                        fields.push(`phone-parent-${i}`);
                    }

                    return await trigger(fields);
                }
            case 4:
                return await trigger(["size", "jersey-number", "size-jersey", "size-short"]);
            default:
                return true;
        }
    }

    return <MainContext.Provider value={{
        certif,
        currentFormTab,
        error,
        errors,
        files,
        handleSubmit,
        licencePrice,
        licenceType,
        loading,
        mutation,
        numberOfParents,
        practice,
        register,
        setCertif,
        setCurrentFormTab,
        setFiles,
        setLoading,
        setLicencePrice,
        setLicenceType,
        setMutation,
        setNumberOfParents,
        setPractice,
        submit,
        validateTab,
    }}>{children}</MainContext.Provider>;
};

export default MainProvider;
